import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, AlertTriangle, Clock, DollarSign, Users, Calendar, X, ChevronRight, Wifi, WifiOff, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../api';
import { toast } from 'sonner';

const NotificationBell = () => {
  const [alerts, setAlerts] = useState([]);
  const [realTimeNotifications, setRealTimeNotifications] = useState([]);
  const [summary, setSummary] = useState({ total: 0, high: 0, medium: 0, low: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Get WebSocket URL based on API URL
  const getWebSocketUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace('/api', '');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || 'anonymous';
    return `${wsProtocol}://${wsHost}/ws/${userId}`;
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.id) {
      console.log('No user ID found, skipping WebSocket connection');
      return;
    }

    const wsUrl = getWebSocketUrl();
    console.log('Connecting to WebSocket:', wsUrl);

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        reconnectAttempts.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);

          if (data.type === 'notification') {
            // Add new notification to the list
            const newNotification = {
              id: `rt-${Date.now()}`,
              title: data.title,
              message: data.message,
              priority: data.priority || 'normal',
              category: data.notificationType || 'system',
              timestamp: data.timestamp,
              isRealTime: true,
              read: false
            };

            setRealTimeNotifications(prev => [newNotification, ...prev].slice(0, 10));
            setUnreadCount(prev => prev + 1);

            // Show toast notification
            if (data.priority === 'high' || data.priority === 'urgent') {
              toast.error(data.title, {
                description: data.message,
                duration: 5000,
              });
            } else {
              toast(data.title, {
                description: data.message,
                duration: 4000,
              });
            }
          } else if (data.type === 'connection') {
            console.log('Connection confirmed:', data.message);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setWsConnected(false);

        // Attempt to reconnect
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connectWebSocket();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }, [getWebSocketUrl]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  // Send ping to keep connection alive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, []);

  // Fetch alerts from API
  useEffect(() => {
    fetchAlerts();
    
    // Refresh alerts every 60 seconds
    const interval = setInterval(fetchAlerts, 60000);
    window.addEventListener('alerts-changed', fetchAlerts);
    return () => { clearInterval(interval); window.removeEventListener('alerts-changed', fetchAlerts); };
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/alerts');
      if (response.data.success) {
        setAlerts(response.data.alerts.slice(0, 10));
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = () => {
    setRealTimeNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'staff':
        return <Users className="w-4 h-4" />;
      case 'clinical':
      case 'documentation_due':
        return <Clock className="w-4 h-4" />;
      case 'billing':
        return <DollarSign className="w-4 h-4" />;
      case 'scheduling':
      case 'visit_reminder':
        return <Calendar className="w-4 h-4" />;
      case 'system':
        return <Bell className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
      case 'normal':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityDot = (priority) => {
    switch (priority) {
      case 'high':
      case 'urgent':
        return 'bg-red-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
      case 'normal':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Combine real-time and API alerts
  const allNotifications = [...realTimeNotifications, ...alerts].slice(0, 15);
  const totalCount = summary.total + realTimeNotifications.filter(n => !n.read).length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen && unreadCount > 0) {
            markAllAsRead();
          }
        }}
        className="relative p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        data-testid="notification-bell-button"
      >
        <Bell className={`w-6 h-6 ${unreadCount > 0 ? 'animate-pulse' : ''}`} />
        {totalCount > 0 && (
          <span className={`absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center text-xs font-bold text-white rounded-full px-1 ${summary.high > 0 || realTimeNotifications.some(n => n.priority === 'high') ? 'bg-red-500' : 'bg-orange-500'}`}>
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-[#ea580c] to-[#f59e0b] text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">Notifications</h3>
                {wsConnected ? (
                  <span className="flex items-center gap-1 text-xs bg-white/20 px-2 py-0.5 rounded-full" data-testid="ws-connected-badge">
                    <Wifi className="w-3 h-3" />
                    Live
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs bg-white/20 px-2 py-0.5 rounded-full opacity-60">
                    <WifiOff className="w-3 h-3" />
                    Offline
                  </span>
                )}
              </div>
              <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-4 mt-2 text-sm">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                {summary.high} Urgent
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-yellow-300 rounded-full"></span>
                {summary.medium} Warning
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-blue-300 rounded-full"></span>
                {summary.low} Info
              </span>
            </div>
          </div>

          {/* Real-time notifications section */}
          {realTimeNotifications.length > 0 && (
            <div className="border-b-2 border-orange-200">
              <div className="px-3 py-2 bg-orange-50 flex items-center justify-between">
                <span className="text-xs font-semibold text-orange-700 uppercase">Real-time Updates</span>
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-orange-600 hover:text-orange-800 flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Mark all read
                </button>
              </div>
              {realTimeNotifications.slice(0, 3).map((notification, index) => (
                <div
                  key={notification.id}
                  className={`p-3 border-b border-orange-100 hover:bg-orange-50/50 transition-colors ${!notification.read ? 'bg-orange-50' : ''}`}
                  data-testid={`realtime-notification-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${getPriorityColor(notification.priority)}`}>
                      {getCategoryIcon(notification.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {!notification.read && <span className={`w-2 h-2 rounded-full ${getPriorityDot(notification.priority)}`}></span>}
                        <h4 className="font-medium text-gray-900 text-sm truncate">{notification.title}</h4>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{notification.message}</p>
                      <span className="text-xs text-gray-400 mt-1 block">Just now</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Regular Alert List */}
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-[#ea580c] border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading alerts...
              </div>
            ) : alerts.length === 0 && realTimeNotifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="font-medium">All caught up!</p>
                <p className="text-sm">No pending alerts</p>
              </div>
            ) : (
              alerts.map((alert, index) => (
                <div
                  key={alert.id || index}
                  className={`p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${alert.priority === 'high' ? 'bg-red-50/50' : ''}`}
                  data-testid={`alert-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${getPriorityColor(alert.priority)}`}>
                      {getCategoryIcon(alert.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${getPriorityDot(alert.priority)}`}></span>
                        <h4 className="font-medium text-gray-900 text-sm truncate">{alert.title}</h4>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{alert.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(alert.priority)}`}>
                          {alert.priority}
                        </span>
                        <span className="text-xs text-gray-400">{alert.category}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {(alerts.length > 0 || realTimeNotifications.length > 0) && (
            <Link
              to="/dashboard/alerts"
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-[#ea580c] font-medium text-sm transition-colors"
              data-testid="view-all-alerts-link"
            >
              View All Alerts
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
